// Progress dialog for the bulk "Create All Work Orders (V2)" action on
// Customer Order Management.
//
// Flow: on open it dry-runs the bulk endpoint to fetch the QUEUE (every
// qualifying new/confirmed order, earliest-due-first) plus the orders that
// don't qualify (already planned in v1/v2). It then processes the queue ONE
// ORDER PER REQUEST, in the server's order — so the due-date stock-netting
// priority survives — updating each row live: queued → processing → done /
// skipped / failed. Ends with a full report summary.
//
// Closing mid-run stops after the in-flight order; everything is idempotent
// (an order that got its WOs won't get them again on a re-run).

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, LinearProgress, Stack, Typography,
} from '@mui/material';
import {
  CheckCircle, ErrorOutline, ExpandLess, ExpandMore, Factory,
  HourglassEmpty, RemoveCircleOutline,
} from '@mui/icons-material';

import { v2 } from '../../../../services/api';

const SKIP_LABELS = {
  already_in_v2: 'Already has V2 work orders',
  active_v1_work_orders: 'Already planned in V1',
  // Not a problem: the order is already satisfied by stock/allocations, so
  // there is genuinely nothing left to manufacture. Worded positively.
  nothing_to_produce: 'Fully covered by stock — no production needed',
};

const skipLabel = (reason) => SKIP_LABELS[reason] || reason;

// A skipped line carries `kind` ∈ covered | already_exploded | failed. Older
// responses omit it — before `kind` existed every skipped line WAS a genuine
// failure, so a missing kind must keep reading as 'failed'.
const skipKind = (s) => (s && s.kind) || 'failed';

function splitLineSkips(lineSkips) {
  const all = lineSkips || [];
  return {
    all,
    covered: all.filter((s) => skipKind(s) === 'covered'),
    already: all.filter((s) => skipKind(s) === 'already_exploded'),
    failed: all.filter((s) => skipKind(s) === 'failed'),
  };
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

function RowIcon({ state }) {
  if (state === 'processing') return <CircularProgress size={18} />;
  if (state === 'done') return <CheckCircle color="success" fontSize="small" />;
  // "covered" is a GOOD outcome (stock already satisfies the order) — it gets
  // a tick, not the amber skip icon.
  if (state === 'covered') return <CheckCircle color="info" fontSize="small" />;
  if (state === 'partial') return <CheckCircle color="warning" fontSize="small" />;
  if (state === 'skipped') return <RemoveCircleOutline color="warning" fontSize="small" />;
  if (state === 'failed') return <ErrorOutline color="error" fontSize="small" />;
  return <HourglassEmpty color="disabled" fontSize="small" />;
}

// The created work orders for one order, grouped by their (first)
// operation — "what was made where" at a glance.
function CreatedWOList({ workOrders }) {
  if (!workOrders || workOrders.length === 0) return null;
  const groups = new Map();
  workOrders.forEach((w) => {
    const first = (w.operations || [])[0] || { code: '?', name: '' };
    const key = first.name ? `${first.code} — ${first.name}` : first.code;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(w);
  });
  return (
    <Box sx={{ ml: 4, mt: 0.25, mb: 0.75 }} data-testid="bulk-v2-wo-list">
      {[...groups.entries()].map(([op, wos]) => (
        <Box key={op} sx={{ mb: 0.5 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
            {op} ({wos.length} WO{wos.length === 1 ? '' : 's'})
          </Typography>
          {wos.map((w) => (
            <Typography
              key={w.wo_number}
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', pl: 1.5 }}
            >
              {w.wo_number} · {w.output_item_name} ({w.output_item_code}) ×
              {String(parseFloat(w.quantity) || 0)}
              {(w.operations || []).length > 1
                ? ` · ${w.operations.length} steps: ${w.operations.map((o) => o.code).join(' → ')}`
                : ''}
              {w.is_top_level ? ' · final assembly' : ' · sub-assembly'}
            </Typography>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function BulkSendToV2Dialog({ open, onClose, onDone }) {
  // phase: loading (fetching queue) | running | done | error
  const [phase, setPhase] = useState('loading');
  const [rows, setRows] = useState([]);        // queue rows with live state
  const [preSkipped, setPreSkipped] = useState([]); // ineligible before start
  const [error, setError] = useState('');
  // Per-order WO breakdowns are collapsed by default — a 20-order run stays
  // scannable; click a completed row to expand its detail.
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (orderId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    (async () => {
      setPhase('loading');
      setRows([]);
      setPreSkipped([]);
      setError('');
      setExpandedIds(new Set());
      let candidates = [];
      try {
        const res = await v2.explode.allNewOrders({ dry_run: true });
        if (cancelled) return;
        candidates = res.data?.candidates || [];
        setPreSkipped(res.data?.orders_skipped || []);
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.data?.error
          || err.response?.data?.detail
          || 'Failed to load the order queue',
        );
        setPhase('error');
        return;
      }

      let current = candidates.map((c) => ({ ...c, state: 'queued', detail: '' }));
      setRows(current);
      setPhase('running');

      const update = (orderId, patch) => {
        current = current.map(
          (r) => (r.order_id === orderId ? { ...r, ...patch } : r),
        );
        setRows(current);
      };

      for (const c of candidates) {
        if (cancelled) return;
        update(c.order_id, { state: 'processing' });
        try {
          // One order per request, in the server-provided (earliest-due)
          // order — keeps the stock-netting priority.
          const res = await v2.explode.allNewOrders({ order_ids: [c.order_id] });
          const exploded = (res.data?.orders_exploded || [])
            .find((o) => o.order_id === c.order_id);
          const skipped = (res.data?.orders_skipped || [])
            .find((o) => o.order_id === c.order_id);
          if (exploded) {
            const n = exploded.work_orders_created || 0;
            const { covered, already, failed } = splitLineSkips(exploded.skipped_lines);
            const parts = [`${plural(n, 'work order')} created`];
            if (covered.length) {
              parts.push(`${plural(covered.length, 'line')} covered from stock (no production needed)`);
            }
            if (already.length) parts.push(`${plural(already.length, 'line')} already sent`);
            // ONLY a genuine failure (e.g. a phantom product) turns the row
            // amber. Covered / already-sent lines are normal outcomes.
            if (failed.length) {
              parts.push(`${plural(failed.length, 'line')} FAILED — ${failed[0].reason}`);
            }
            update(c.order_id, {
              state: failed.length ? 'partial' : 'done',
              work_orders: exploded.work_orders || [],
              woCount: n,
              coveredLines: covered.length,
              detail: parts.join(' · '),
            });
          } else if (skipped) {
            // Per-line outcomes carry the real reason in skipped_lines — show
            // that over the generic label.
            const { all, covered, already, failed } = splitLineSkips(skipped.skipped_lines);
            let state = 'skipped';
            let detail;
            if (failed.length) {
              detail = `No work orders created — ${failed[0].reason}`
                + (failed.length > 1 ? ` (+${failed.length - 1} more)` : '');
            } else if (all.length && covered.length === all.length) {
              // Every line is already satisfied by stock — a good outcome.
              state = 'covered';
              detail = `Fully covered by stock — no production needed (${plural(covered.length, 'line')})`;
            } else if (all.length && !covered.length) {
              detail = `Already sent — ${plural(already.length, 'line')} already have work orders`;
            } else if (all.length) {
              state = 'covered';
              detail = `No production needed — ${plural(covered.length, 'line')} covered from stock`
                + (already.length ? `, ${plural(already.length, 'line')} already sent` : '');
            } else {
              if (skipped.reason === 'nothing_to_produce') state = 'covered';
              detail = skipLabel(skipped.reason);
            }
            update(c.order_id, { state, detail, woCount: 0, coveredLines: covered.length });
          } else {
            update(c.order_id, {
              state: 'skipped', detail: 'No longer eligible', woCount: 0,
            });
          }
        } catch (err) {
          update(c.order_id, {
            state: 'failed',
            detail: err.response?.data?.error
              || err.response?.data?.detail
              || 'Request failed',
          });
        }
      }

      if (!cancelled) {
        setPhase('done');
        if (onDone) onDone();
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const processedCount = rows.filter(
    (r) => r.state !== 'queued' && r.state !== 'processing',
  ).length;
  const doneCount = rows.filter(
    (r) => r.state === 'done' || r.state === 'partial',
  ).length;
  const partialCount = rows.filter((r) => r.state === 'partial').length;
  const skipCount = rows.filter((r) => r.state === 'skipped').length;
  const coveredOrderCount = rows.filter((r) => r.state === 'covered').length;
  const failCount = rows.filter((r) => r.state === 'failed').length;
  // Carried numerically on each row — parsing it back out of the detail string
  // meant any wording change silently zeroed the total.
  const totalWOs = rows.reduce((t, r) => t + (r.woCount || 0), 0);
  const coveredLineCount = rows.reduce((t, r) => t + (r.coveredLines || 0), 0);
  // Only genuine failures make the run amber — a run where everything was
  // already covered by stock is a success.
  const hasRealFailures = failCount > 0 || partialCount > 0;

  return (
    <Dialog
      open={open}
      onClose={phase === 'running' ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="bulk-v2-dialog"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Factory /> Create All Work Orders (V2)
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {phase === 'loading' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress size={22} />
            <Typography variant="body2">Finding orders that need work orders…</Typography>
          </Box>
        )}

        {phase === 'error' && (
          <Alert severity="error" data-testid="bulk-v2-error">{error}</Alert>
        )}

        {(phase === 'running' || phase === 'done') && (
          <>
            {phase === 'running' && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant={rows.length ? 'determinate' : 'indeterminate'}
                  value={rows.length ? (processedCount / rows.length) * 100 : 0}
                />
                <Typography variant="caption" color="text.secondary">
                  {processedCount} of {rows.length} orders processed — closing
                  stops after the current order
                </Typography>
              </Box>
            )}

            {phase === 'done' && (
              <Alert
                severity={hasRealFailures ? 'warning' : 'success'}
                sx={{ mb: 2 }}
                data-testid="bulk-v2-summary"
              >
                {plural(totalWOs, 'work order')} created across{' '}
                {plural(doneCount, 'order')}
                {coveredLineCount > 0 ? ` · ${coveredLineCount} lines covered from stock` : ''}
                {coveredOrderCount > 0
                  ? ` · ${plural(coveredOrderCount, 'order')} fully covered by stock`
                  : ''}
                {partialCount > 0 ? ` · ${partialCount} with failed lines` : ''}
                {skipCount > 0 ? ` · ${skipCount} skipped` : ''}
                {failCount > 0 ? ` · ${failCount} FAILED` : ''}
              </Alert>
            )}

            {rows.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No orders need V2 work orders right now.
              </Alert>
            )}

            <Stack spacing={0.75} data-testid="bulk-v2-queue">
              {rows.map((r) => {
                const hasDetail = (r.work_orders || []).length > 0;
                const isOpen = expandedIds.has(r.order_id);
                return (
                  <React.Fragment key={r.order_id}>
                    <Box
                      data-testid={`bulk-v2-row-${r.order_id}`}
                      onClick={hasDetail ? () => toggleExpanded(r.order_id) : undefined}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        p: 0.75, borderRadius: 1, border: '1px solid',
                        borderColor: r.state === 'processing' ? 'primary.main' : 'divider',
                        opacity: r.state === 'queued' ? 0.65 : 1,
                        cursor: hasDetail ? 'pointer' : 'default',
                        '&:hover': hasDetail ? { backgroundColor: 'action.hover' } : {},
                      }}
                    >
                      <RowIcon state={r.state} />
                      <Chip label={r.order_number} size="small" color="primary" sx={{ fontWeight: 700 }} />
                      <Typography variant="caption" color="text.secondary">
                        {r.due_date ? `due ${new Date(r.due_date).toLocaleDateString()}` : 'no due date'}
                        {r.line_count != null ? ` · ${r.line_count} line${r.line_count === 1 ? '' : 's'}` : ''}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={
                          r.state === 'done' ? 'success.main'
                            : r.state === 'covered' ? 'info.main'
                              : r.state === 'failed' ? 'error.main'
                                : (r.state === 'skipped' || r.state === 'partial')
                                  ? 'warning.main' : 'text.secondary'
                        }
                      >
                        {r.state === 'processing' ? 'Creating…' : r.detail}
                      </Typography>
                      {hasDetail && (isOpen
                        ? <ExpandLess fontSize="small" color="action" />
                        : <ExpandMore fontSize="small" color="action" />)}
                    </Box>
                    {/* Full breakdown (per operation) — collapsed by default
                        so a 20-order run stays scannable. */}
                    {hasDetail && (
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <CreatedWOList workOrders={r.work_orders} />
                      </Collapse>
                    )}
                  </React.Fragment>
                );
              })}
            </Stack>

            {preSkipped.length > 0 && (
              <Box sx={{ mt: 2 }} data-testid="bulk-v2-preskipped">
                <Typography variant="subtitle2" gutterBottom>
                  Not eligible ({preSkipped.length})
                </Typography>
                <Stack spacing={0.5}>
                  {preSkipped.map((s) => (
                    <Typography key={s.order_id} variant="caption" color="text.secondary">
                      {s.order_number} — {skipLabel(s.reason)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          data-testid="bulk-v2-close"
          color={phase === 'running' ? 'warning' : 'primary'}
        >
          {phase === 'running' ? 'Stop after current' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
